import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ActionDialog } from "@/components/dashboard/ActionDialog";
import RoutingExplainer from "@/components/dashboard/RoutingExplainer";
import { RowBin, ClearArea } from "@/components/admin/Purge";
import { Plus, Check, X, Clock, ShieldCheck, Loader2, Building2, BarChart3, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending_payment: { label: "Awaiting payment", color: "text-slate-600", bg: "bg-slate-100",       Icon: Clock },
  deposit_paid:    { label: "Pending review",  color: "text-[#0055FF]", bg: "bg-[#0055FF]/10",     Icon: ShieldCheck },
  live:            { label: "Live",             color: "text-emerald-700", bg: "bg-emerald-50",     Icon: Check },
  rejected:        { label: "Rejected",         color: "text-rose-700",    bg: "bg-rose-50",        Icon: X },
};

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [manageCampaign, setManageCampaign] = useState(null);
  const [analyticsCampaign, setAnalyticsCampaign] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);

  const refresh = async () => {
    const [c, r] = await Promise.all([
      api.get("/admin/campaigns").then(x => x.data).catch(() => []),
      api.get("/admin/reservations").then(x => x.data).catch(() => []),
    ]);
    setRows(c);
    setReservations(r);
  };

  useEffect(() => {
    refresh();
    api.get("/catalog/sectors").then(r => setSectors(r.data || [])).catch(() => setSectors([]));
  }, []);

  const openAnalytics = async (c) => {
    setAnalyticsCampaign(c);
    setAnalyticsData(null);
    try {
      const { data } = await api.get(`/admin/campaigns/${c.id}/analytics`);
      setAnalyticsData(data);
    } catch (e) {
      toast.error("Could not load analytics");
    }
  };

  const approve = async (rid) => {
    setBusy(rid);
    try {
      await api.post(`/admin/reservations/${rid}/approve`);
      toast.success("Campaign approved — buyer can see it live in their dashboard.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve reservation.");
    } finally { setBusy(null); }
  };

  const reject = async (rid) => {
    const reason = window.prompt("Rejection reason (visible to buyer):", "Did not meet compliance requirements.");
    if (!reason) return;
    setBusy(rid);
    try {
      await api.post(`/admin/reservations/${rid}/reject`, { reason });
      toast.success("Reservation rejected.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject reservation.");
    } finally { setBusy(null); }
  };

  // Show pending+deposit_paid reservations at the top, then live campaigns
  const pendingRes = reservations.filter(r => r.status === "deposit_paid" || r.status === "pending_payment");
  const recentRes  = reservations.filter(r => r.status === "live" || r.status === "rejected").slice(0, 6);

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Campaigns"
        subtitle="Active acquisition campaigns and pending reservations. Approving a reservation spawns a live campaign and lead routing begins automatically."
        actions={<Button className="rounded-full" onClick={() => setOpen(true)} data-testid="admin-new-campaign"><Plus className="size-4" /> New campaign</Button>}
      />

      <RoutingExplainer audience="admin" storageKey="routing-explainer-admin-campaigns" />

      <ActionDialog
        open={open}
        onOpenChange={setOpen}
        title="Create a campaign"
        description="Spin up a new sector campaign and assign buyers."
        endpoint="/admin/campaigns"
        submitLabel="Create campaign"
        testidPrefix="campaign"
        onSuccess={refresh}
        fields={[
          { name: "name", label: "Campaign name", required: true, placeholder: "e.g. Q1 Mortgage Push — UK", type: "text" },
          { name: "sector", label: "Sector", required: true, type: "select", options: sectors.map(s => ({ value: s.slug, label: s.name })), defaultValue: "mortgages" },
          { name: "buyers", label: "Buyer count", required: true, type: "number", defaultValue: 3, min: 1, max: 20 },
        ]}
      />

      {/* ===== Pending Reservations ===== */}
      {pendingRes.length > 0 && (
        <div data-testid="admin-pending-reservations">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-grid place-items-center size-8 rounded-lg bg-[#0F172A] text-white">
              <ShieldCheck className="size-4" strokeWidth={1.6} />
            </span>
            <div>
              <h3 className="text-base font-medium text-slate-900">Pending reservations</h3>
              <p className="text-xs text-slate-500">Online orders awaiting compliance review.</p>
            </div>
            <span className="ml-auto text-[10px] font-mono uppercase tracking-widest bg-[#0055FF]/10 text-[#0055FF] px-2 py-1 rounded-full">{pendingRes.length} pending</span>
            <ClearArea area="reservations" onDone={refresh} label="Clear reservations" />
          </div>
          <div className="space-y-3">
            {pendingRes.map((r) => (
              <div key={r.id} className="flex items-start gap-1.5">
                <div className="min-w-0 flex-1">
                  <ReservationRow res={r} busy={busy === r.id} onApprove={() => approve(r.id)} onReject={() => reject(r.id)} />
                </div>
                <div className="pt-4"><RowBin area="reservations" id={r.id} name={r.company} onDone={refresh} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Live Campaigns ===== */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-grid place-items-center size-8 rounded-lg bg-[#0F172A] text-white">
            <Building2 className="size-4" strokeWidth={1.6} />
          </span>
          <div>
            <h3 className="text-base font-medium text-slate-900">Active campaigns</h3>
            <p className="text-xs text-slate-500">{rows.length} live across sectors.</p>
          </div>
          <div className="ml-auto"><ClearArea area="campaigns" onDone={refresh} label="Clear campaigns" /></div>
        </div>
        <div className="grid lg:grid-cols-3 gap-5">
          {rows.map((c) => (
            <Card key={c.id} className="rounded-xl shadow-soft border-slate-200 bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <span className="text-[10px] uppercase tracking-wider rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-0.5">Live</span>
                </div>
                <div className="mt-1 text-xs text-slate-500 capitalize">{(c.sector || "").replace("-", " ")} · {c.buyers} buyers</div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <S l="Delivered (MTD)" v={(c.delivered_mtd || 0).toLocaleString()} />
                  <S l="Spend (MTD)" v={`£${(c.spend_mtd || 0).toLocaleString()}`} />
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => setManageCampaign(c)} data-testid={`campaign-manage-${c.id.slice(0,8)}`}>
                    <Settings2 className="size-3.5" /> Manage
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-full" onClick={() => openAnalytics(c)} data-testid={`campaign-analytics-${c.id.slice(0,8)}`}>
                    <BarChart3 className="size-3.5" /> Analytics
                  </Button>
                  <div className="ml-auto"><RowBin area="campaigns" id={c.id} name={c.name} onDone={refresh} /></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ===== Recent activity ===== */}
      {recentRes.length > 0 && (
        <div>
          <h3 className="text-base font-medium text-slate-900 mb-3">Recent reservation activity</h3>
          <div className="space-y-2">
            {recentRes.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <StatusPill status={r.status} />
                  <div className="min-w-0 truncate">
                    <span className="font-medium text-slate-900">{r.company}</span>
                    <span className="text-slate-500"> · {(r.config?.sector || "").replace("-", " ")} · {r.config?.volume?.toLocaleString() || "—"} leads/mo</span>
                  </div>
                </div>
                <span className="text-[11px] font-mono text-slate-400 shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Manage campaign dialog ===== */}
      <CampaignManageDialog campaign={manageCampaign} sectors={sectors} onClose={() => setManageCampaign(null)} onSaved={() => { setManageCampaign(null); refresh(); }} />

      {/* ===== Analytics drawer ===== */}
      <CampaignAnalyticsSheet open={!!analyticsCampaign} campaign={analyticsCampaign} data={analyticsData} onClose={() => { setAnalyticsCampaign(null); setAnalyticsData(null); }} />
    </div>
  );
}

function CampaignManageDialog({ campaign, sectors, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [sector, setSector] = useState("mortgages");
  const [buyers, setBuyers] = useState(1);
  const [live, setLive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (campaign) {
      setName(campaign.name || "");
      setSector(campaign.sector || "mortgages");
      setBuyers(campaign.buyers || 1);
      setLive(campaign.live !== false);
    }
  }, [campaign]);

  if (!campaign) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/campaigns/${campaign.id}`, {
        name: name.trim(),
        sector,
        buyers: parseInt(buyers, 10) || 1,
        live,
      });
      toast.success("Campaign updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete campaign "${campaign.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/campaigns/${campaign.id}`);
      toast.success("Campaign deleted");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open={!!campaign} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="campaign-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="size-5 text-[#0055FF]" /> Manage campaign</DialogTitle>
          <DialogDescription>Update campaign config. Changes apply on the next inbound lead.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-10" data-testid="campaign-manage-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sector</Label>
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger data-testid="campaign-manage-sector" className="mt-1 h-10"><SelectValue placeholder="Sector" /></SelectTrigger>
                <SelectContent>{sectors.map(s => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Buyers</Label>
              <Input type="number" min={1} max={100} value={buyers} onChange={(e) => setBuyers(e.target.value)} className="mt-1 h-10 tabular-nums" data-testid="campaign-manage-buyers" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <div>
              <div className="text-[13px] font-medium text-slate-900">Live</div>
              <div className="text-[11.5px] text-slate-500">Pause to stop routing leads to this campaign.</div>
            </div>
            <Switch checked={live} onCheckedChange={setLive} data-testid="campaign-manage-live" />
          </div>
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={remove} className="rounded-full text-rose-600 hover:bg-rose-50 mr-auto" data-testid="campaign-manage-delete"><Trash2 className="size-3.5" /> Delete</Button>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="campaign-manage-save">
            {busy ? <><Loader2 className="size-4 animate-spin" /> Saving…</> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignAnalyticsSheet({ open, campaign, data, onClose }) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="campaign-analytics-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><BarChart3 className="size-5 text-[#0055FF]" /> {campaign?.name || ""} — analytics</SheetTitle>
          <SheetDescription>Last 7 days · delivery, accept-rate, spend.</SheetDescription>
        </SheetHeader>
        {!data ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="size-5 animate-spin mx-auto mb-2" /> Loading…</div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">Delivered</div><div className="font-semibold tabular-nums text-slate-900 mt-0.5">{data.totals?.delivered ?? 0}</div></div>
              <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">Accept rate</div><div className="font-semibold tabular-nums text-slate-900 mt-0.5">{data.totals?.accept_rate_pct ?? 0}%</div></div>
              <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">Spend</div><div className="font-semibold tabular-nums text-slate-900 mt-0.5">£{(data.totals?.spend_gbp ?? 0).toFixed(2)}</div></div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Daily breakdown</div>
              {data.daily?.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-sm italic">No leads in the last 7 days.</div>
              ) : (
                <div className="space-y-1.5">
                  {data.daily.map((d) => (
                    <div key={d.day} className="flex items-center justify-between text-[13px] rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <span className="text-slate-700 font-mono">{d.day}</span>
                      <span className="text-slate-500">{d.delivered} delivered</span>
                      <span className="text-slate-500">{d.accepted} accepted</span>
                      <span className="text-slate-900 font-mono tabular-nums">£{(d.spend || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ReservationRow({ res, busy, onApprove, onReject }) {
  return (
    <Card className="rounded-xl border-slate-200 bg-white shadow-soft" data-testid={`reservation-${res.id}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h4 className="font-medium text-slate-900 text-[15px] leading-tight">{res.company}</h4>
              <StatusPill status={res.status} />
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {res.contact_name} · <span className="font-mono">{res.user_email}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <S l="Sector" v={(res.config?.sector || "").replace("-", " ")} cap />
              <S l="Tier" v={res.config?.tier || "—"} cap />
              <S l="Volume" v={`${(res.config?.volume || 0).toLocaleString()} / mo`} />
              <S l="Est. monthly" v={`£${(res.config?.estimate_total_gbp || 0).toLocaleString()}`} />
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={onReject}
              disabled={busy || res.status === "pending_payment"}
              className="rounded-full text-rose-700 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
              data-testid={`reservation-reject-${res.id}`}
            >
              <X className="size-3.5" /> Reject
            </Button>
            <Button
              size="sm"
              onClick={onApprove}
              disabled={busy || res.status === "pending_payment"}
              className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50"
              data-testid={`reservation-approve-${res.id}`}
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
              {res.status === "pending_payment" ? "Awaiting payment" : "Approve · go live"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending_payment;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full ${meta.bg} ${meta.color} px-2 py-0.5`}>
      <Icon className="size-3" strokeWidth={2.2} /> {meta.label}
    </span>
  );
}

function S({ l, v, cap }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
      <div className={`mt-0.5 text-[14px] font-semibold text-slate-900 tabular-nums ${cap ? "capitalize" : ""}`}>{v}</div>
    </div>
  );
}
